import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { analytics } from "../../helpers/analytics";
import 'vanilla-cookieconsent/dist/cookieconsent.css';

import { Header, OnboardingHeader } from "../Header";
import { PostLoader } from "../PostLoader";
import { Notification } from "../Notification";
import {
  HIDE_ONBOARDING,
  refreshSubscriptionStatus,
  subscribeToAuthChanges,
} from "../../helpers/magic";
import { UserContext } from "../../context/userContext";
import { isIos, canShare } from "../../helpers/shared";
import { StrokeIcon } from "../../Icons";
import { PriceContext } from "../../context/priceContext";
import {
  OnboardingContext,
  OnboardingValue,
} from "../../context/onboardingContext";
import { SuccessSubscriptionModal } from "../SuccessSubscriptionModal";
import {
  trackStartTrial,
  trackSubscriptionPurchase,
} from "../../helpers/metaPixel";

type Props = {
  children: React.ReactChild;
};

type User = {
  email: string;
  id: string;
  hasPremium: boolean;
  subscriptionEnd: number | null;
  isNewSubscriber: boolean;
  isCanceled: boolean | null;
  plan: {
    amount: number;
    interval: string;
    isTrial: boolean;
  } | null;
  subscriptionAfterTrail: boolean;
  subscriptionSource?: string;
};

type Price = {
  weekly: {
    amount?: number;
    price?: string;
  };
  annually: {
    amount?: number;
    price?: string;
  };
  trial: {
    amount?: number;
    price?: string;
  };
};

const Layout = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(null);
  const [price, setPrice] = useState<Price | null>(null);
  const [isPostLoaderVisible, setPostLoaderVisible] = useState(false);
  const [isWarningVisible, setWarningVisible] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const lastSubscriptionRefreshRef = useRef(0);

  const navigate = useNavigate();

  const handleSuccessClose = () => {
    analytics.track("Subscription Success Closed");
    setIsSuccessOpen(false);
    const path = /\/subscription-activated/;
    navigate(window.location.pathname.replace(path, ""), { replace: true });
  };

  const refreshCurrentSubscription = useCallback(
    async (source = "page_open") => {
      const now = Date.now();
      if (now - lastSubscriptionRefreshRef.current < 10000) {
        return;
      }
      lastSubscriptionRefreshRef.current = now;

      try {
        const refreshedUser = await refreshSubscriptionStatus(setUser);
        analytics.track("Subscription Status Refreshed", {
          authenticated: Boolean(refreshedUser?.email),
          has_premium: refreshedUser?.hasPremium,
          source,
          subscription_source: refreshedUser?.subscriptionSource,
        });
      } catch (error) {
        analytics.track("Subscription Status Refresh Failed", {
          error_message: error instanceof Error ? error.message : String(error),
          source,
        });
        console.error(error);
      }
    },
    []
  );

  useEffect(() => {
    const getProducts = async () => {
      try {
        analytics.track("Products Fetch Started");
        const data = await fetch("/.netlify/functions/get-products")
          .then((res) => res.json())
          // eslint-disable-next-line no-console
          .catch((err) => console.error(err));

        return data;
      } catch (e) {
        analytics.track("Products Fetch Failed", {
          error_message: e instanceof Error ? e.message : String(e),
        });
        console.error(e);
        throw e;
      }
    };

    try {
      getProducts().then((r) => {
        if (!Array.isArray(r)) {
          analytics.track("Products Fetch Failed", {
            error_message: "Product response was not an array",
          });
          return;
        }

        const findPrice = (priceId: string | undefined, interval: string) =>
          r.find((item: any) => priceId && item.price === priceId) ||
          r.find((item: any) => item.interval === interval);
        const weekly = findPrice(process.env.REACT_APP_STRIPE_WEEKLY, "week");
        const annually = findPrice(process.env.REACT_APP_STRIPE_ANNUALLY, "year");
        const trial = r.find(
          (item: any) => item.price === process.env.REACT_APP_STRIPE_TRIAL
        );

        setPrice({
          weekly: {
            amount: weekly?.amount,
            price: weekly?.price,
          },
          annually: {
            amount: annually?.amount,
            price: annually?.price,
          },
          trial: {
            amount: trial?.amount,
            price: trial?.price,
          },
        });
        analytics.track("Products Loaded", {
          weekly_price_available: Boolean(weekly?.price),
          annual_price_available: Boolean(annually?.price),
          trial_price_available: Boolean(trial?.price),
          products_count: r.length,
        });
      });
    } catch (e) {
      analytics.track("Products Fetch Failed", {
        error_message: e instanceof Error ? e.message : String(e),
      });
      console.error(e);
    }
  }, []);

  useEffect(() => {
    const validateUser = async () => {
      await refreshCurrentSubscription("page_open");
    };
    validateUser();
    const subscription = subscribeToAuthChanges(setUser);

    return () => {
      subscription.unsubscribe();
    };
  }, [refreshCurrentSubscription]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshCurrentSubscription("page_visible");
      }
    };

    const handleFocus = () => {
      refreshCurrentSubscription("window_focus");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refreshCurrentSubscription]);

  useEffect(() => {
    if (window.location.href.includes("redirect") && !user) {
      analytics.track("Auth Redirect Pending");
      setPostLoaderVisible(true);
    } else if (user) {
      const path = /\/redirect.*/;
      setPostLoaderVisible(false);
      analytics.track("Auth Redirect Completed");

      navigate(window.location.pathname.replace(path, ""));
    }
  }, [setPostLoaderVisible, user, navigate]);

  useEffect(() => {
    if (isIos() && !canShare()) {
      analytics.track("IOS Install Warning Shown", {
        source: "layout",
      });
      setWarningVisible(true);
    }
  }, []);

  useEffect(() => {
    try {
      if (window.location.pathname.includes("/subscription-activated")) {
        setIsSuccessOpen(true);
        sessionStorage.setItem(HIDE_ONBOARDING, "true");
        if (window.location.pathname.includes("onboarding")) {
          navigate("subscription-activated", { replace: true });
        }
        setTimeout(() => {
          const purchaseValue = localStorage.getItem("last_purchase");

          analytics?.track("Subscription activated", {
            premium: true,
            trial: false,
            purchase_value: purchaseValue,
            source: "checkout_redirect",
          });
          trackSubscriptionPurchase(purchaseValue);

          // @ts-ignore
          gtag('event', 'purchase', {
            'transaction_id': Math.random() * 100000,
            value: 0,
            currency: "USD",
            items: [
            {
              item_id: "subscription",
              item_name: "subscription",
              index: 0,
              item_brand: "subscription",
              item_category: "subscription",
              item_list_id: "related_products",
              item_list_name: "Related Products",
              item_variant: "green",
              price: 0,
              quantity: 1
            }]
          });
        }, 100);
      } else if (window.location.pathname.includes("/trial-activated")) {
        const path = /\/trial-activated.*/;
        navigate(window.location.pathname.replace(path, ""), { replace: true });
        setIsSuccessOpen(true);
        sessionStorage.setItem(HIDE_ONBOARDING, "true");
        setTimeout(() => {
          analytics?.track("Subscription activated", {
            premium: true,
            trial: true,
            purchase_value: localStorage.getItem("last_purchase"),
            source: "checkout_redirect",
          });
          trackStartTrial(localStorage.getItem("last_purchase"));
        }, 100);
      }
    } catch (err) {
      console.error(err);
    }
  }, [navigate]);

  useEffect(() => {
    if (isWarningVisible) {
      const timer = setTimeout(() => setWarningVisible(false), 4000);
      return () => clearTimeout(timer);
    }
    return () => {};
  });

  useEffect(() => {
    if (user && user.subscriptionAfterTrail && user.email) {
      const isSent = localStorage.getItem(`${user.email}_after_trial`);
      if (!isSent) {
        localStorage.setItem(`${user.email}_after_trial`, "true");
        analytics.track("Subscription activated", {
          premium: true,
          trial: false,
          amount: user.plan?.amount,
          interval: user.plan?.interval,
          source: "subscription_after_trial",
        });
        trackSubscriptionPurchase(user.plan?.amount, "subscription_after_trial");
      }
    }
  }, [user]);

  const value = useMemo(() => ({ user, setUser }), [user, setUser]);
  const priceValue = useMemo(() => ({ price, setPrice }), [price, setPrice]);
  const ONBOARDING_EMAIL = "ONBOARDING_EMAIL";
  const onboardingEmail = localStorage.getItem(ONBOARDING_EMAIL);
  const [onboardingValue, setOnboardingValue] = useState<OnboardingValue>({
    about: "I just learning how to draw",
    looking: [],
    email: onboardingEmail || "",
    plan: "",
  });

  useEffect(() => {
    localStorage.setItem(ONBOARDING_EMAIL, onboardingValue.email);
  }, [onboardingValue.email]);

  return (
    <div className="w-full h-full">
      <UserContext.Provider value={value}>
        <PriceContext.Provider value={priceValue}>
          <OnboardingContext.Provider
            value={{ value: onboardingValue, set: setOnboardingValue }}
          >
            {window.location.href.includes("onboarding") ? (
              <OnboardingHeader />
            ) : (
              <Header />
            )}
            {children}
            <PostLoader
              isOpen={isPostLoaderVisible}
              onClose={() => setPostLoaderVisible(true)}
            />
            {isWarningVisible && (
              <Notification
                icon={<StrokeIcon className="w-[20px]" />}
                text="Content install is working only on iOS 15 and newer"
              />
            )}

            <SuccessSubscriptionModal
              isOpen={isSuccessOpen}
              onClose={handleSuccessClose}
            />
          </OnboardingContext.Provider>
        </PriceContext.Provider>
      </UserContext.Provider>
    </div>
  );
};

export { Layout };
